import React from "react"

import styled, { css } from "styled-components"

import { borderRadius, colors, spacing, sizeNumeric } from "@izettle/otto"

import { SemanticState, InputSize } from "../../enumerations"

import { InputBaseContainerProps } from "./types"

const mixinInputBaseContainerSize = css<InputBaseContainerProps>`
  ${({ isFocused, isDisabled, semanticState, size }) => {
    const height = {
      [InputSize.Small]: sizeNumeric.xMedium,
      [InputSize.Medium]: sizeNumeric.medium,
      [InputSize.Large]: sizeNumeric.large,
    }[size]

    return !isDisabled && (isFocused || semanticState !== SemanticState.Default)
      ? css`
          border-width: 2px;
          height: ${height}px;
        `
      : css`
          border-width: 1px;
          /* stylelint-disable-next-line @izettle/spacing */
          padding: 1px;
          height: ${height}px;
        `
  }}
`

// Some of the colors below are duplicated and render the logical statements useless
// This is due to the lack of proper semantic tokens for the respective states
// They will be updated in the next release of the Otto library

const semanticColors = {
  [SemanticState.Warning]: css`
    border-color: ${colors.border.warning};
    &:hover {
      border-color: ${colors.border.warning};
    }
  `,
  [SemanticState.Error]: css`
    border-color: ${colors.border.error};
    &:hover {
      border-color: ${colors.border.error};
    }
  `,
  [SemanticState.Success]: css`
    border-color: ${colors.border.success};
    &:hover {
      border-color: ${colors.border.success};
    }
  `,
  [SemanticState.Default]: css`
    border-color: ${colors.border.interactive};
    &:hover {
      /* stylelint-disable-next-line @izettle/border-color */
      border-color: ${colors.icon.secondary};
    }
  `,
}

const mixinInputBaseContainerPalette = ({
  semanticState,
  isFocused,
  isDisabled,
  isErrorStateOverriden,
}: InputBaseContainerProps) => {
  if (isErrorStateOverriden) {
    return semanticColors[SemanticState.Error]
  }
  if (isDisabled) {
    return css`
      cursor: not-allowed;
      border-color: ${colors.border.divider};
      background-color: ${colors.background.groupedPrimary};
    `
  } else if (isFocused) {
    return css`
      border-color: ${colors.action.primary.border.default};
    `
  }

  const semanticColorKey: keyof typeof semanticColors =
    semanticState ?? SemanticState.Default

  return semanticColors[semanticColorKey]
}

export const InputBaseContainer = React.memo(styled.div<InputBaseContainerProps>`
  position: relative;
  border-style: solid;
  display: flex;
  outline: none;
  border-radius: ${borderRadius.medium};
  min-width: 100%;
  max-width: 100%;
  background-color: ${colors.background.default};
  transition: border-color 0.1s cubic-bezier(0.25, 0.1, 0.25, 1);
  ${mixinInputBaseContainerSize}
  ${mixinInputBaseContainerPalette}
  ${({ isLoading, isDisabled }) =>
    isDisabled ? "cursor: not-allowed;" : isLoading && "cursor: progress;"}
`)

export const InputBaseContentContainer = styled.div`
  display: flex;
  flex: 1;
  position: relative;
  min-width: 0;
`

export const InputBaseControlsContainer = styled.div`
  display: flex;
`

export const LoaderContainer = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  padding-right: ${spacing.small};
`

export const InputPrefix = styled.div<{ isVisible?: boolean }>`
  display: none;
  color: ${colors.text.secondary};

  ${({ isVisible }) => {
    return isVisible
      ? css`
          display: flex;
          align-items: center;
          justify-content: center;
          margin-left: ${spacing.xxSmall};
          padding-left: ${spacing.xxSmall};
        `
      : css`
          display: none;
        `
  }}
`
